import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutTextInput } from 'components/design-system/BreakoutTextInput'
import { query } from 'config/api'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

// Must match the main queue worker's CONCURRENCY and the autoscaler's
// JOBS_PER_INSTANCE (docs/systems/autoscaling.md).
const JOBS_PER_WORKER_INSTANCE = 20

// Same limits as scheduleLoadTestValidator on the server.
const MAX_JOBS = 500
const MAX_SECONDS_PER_JOB = 60

const schema = z.object({
  count: z.coerce
    .number()
    .int()
    .min(1, 'At least 1 job')
    .max(MAX_JOBS, `At most ${MAX_JOBS} jobs`),
  // A cleared field would coerce to 0 and queue jobs that finish instantly.
  secondsPerJob: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.coerce
      .number({ message: 'Enter a number of seconds' })
      .int()
      .min(0, 'Cannot be negative')
      .max(MAX_SECONDS_PER_JOB, `At most ${MAX_SECONDS_PER_JOB} seconds`)
  ),
})

type FormValues = z.infer<typeof schema>

export function AdminLoadTestSection() {
  const load = useMutation(query.api.v1.test.load.mutationOptions())

  const { control, handleSubmit, watch } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { count: 200, secondsPerJob: 30 },
  })

  const onSubmit = handleSubmit(({ count, secondsPerJob }) =>
    load.mutate({ body: { count, durationMs: secondsPerJob * 1000 } })
  )

  const minutesOnOneInstance = estimateMinutesOnOneInstance(
    watch('count'),
    watch('secondsPerJob')
  )

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-4 rounded-3xl bg-surface p-6"
    >
      <div>
        <div className="text-title-large">Worker autoscaling load test</div>
        <div className="mt-1 text-body-medium text-on-surface-var">
          Queues test jobs that only wait for the given time. One worker
          instance runs {JOBS_PER_WORKER_INSTANCE} jobs at once, so the
          autoscaler adds instances when more than {JOBS_PER_WORKER_INSTANCE}{' '}
          jobs are waiting or running. Not available in production.
        </div>
      </div>

      <div className="flex gap-4">
        <Controller
          control={control}
          name="count"
          render={({ field, fieldState }) => (
            <BreakoutTextInput
              {...field}
              type="number"
              label="Number of jobs"
              testId="load-test-count"
              error={fieldState.error}
            />
          )}
        />
        <Controller
          control={control}
          name="secondsPerJob"
          render={({ field, fieldState }) => (
            <BreakoutTextInput
              {...field}
              type="number"
              label="Seconds per job"
              testId="load-test-seconds"
              error={fieldState.error}
            />
          )}
        />
      </div>

      {minutesOnOneInstance !== null && (
        <div className="text-body-medium text-on-surface-var">
          Keeps one worker instance busy for about {minutesOnOneInstance} min.
        </div>
      )}

      <BreakoutButton
        type="submit"
        disabled={load.isPending}
        kind="accent"
        size="large"
        data-testid="load-test-start"
      >
        {load.isPending ? 'Queueing…' : 'Start load test'}
      </BreakoutButton>

      {load.data && (
        <div className="text-body-medium">
          Queued {load.data.count} jobs. Watch &quot;Worker instances&quot; on
          the Breakout Server - Overview dashboard in Cloud Monitoring.
        </div>
      )}

      {load.isError && (
        <div className="text-body-medium text-core-error">
          Error: {load.error.message}
        </div>
      )}
    </form>
  )
}

// Jobs run in batches of JOBS_PER_WORKER_INSTANCE, each batch taking
// secondsPerJob. Null while an input is empty or out of range.
function estimateMinutesOnOneInstance(count: number, secondsPerJob: number) {
  const parsed = schema.safeParse({ count, secondsPerJob })
  if (!parsed.success) return null
  const batches = Math.ceil(parsed.data.count / JOBS_PER_WORKER_INSTANCE)
  const totalSeconds = batches * parsed.data.secondsPerJob
  return Math.round((totalSeconds / 60) * 10) / 10
}
